import { Task } from "@/api.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTrips } from "@/api/trips";
import { useAssignTasksToTrip } from "@/api/tasks";
import { getTaskTypeDisplay } from "./tasks.helper";

interface SelectedTasksListProps {
  tasks: Task[];
  onClose?: () => void;
}

export function SelectedTasksList({ tasks, onClose }: SelectedTasksListProps) {
  const { toast } = useToast();
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const { data: trips } = useTrips({ status: "ACTIVE,PLANNED" });

  const assignTasksMutation = useAssignTasksToTrip({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tasks assigned to trip successfully",
      });
      onClose?.();
    },
    onError: (error) => {
      console.error("Error assigning tasks to trip:", error);
      toast({
        title: "Error",
        description: "Failed to assign tasks to trip",
        variant: "destructive",
      });
    },
  });

  // Set all tasks as selected by default
  useEffect(() => {
    setSelectedTaskIds(tasks.map((task) => task.id));
  }, [tasks]);

  const handleTripSelect = (tripId: string) => {
    setSelectedTripId(tripId);
  };

  const handleConfirmAssign = () => {
    if (!selectedTripId) return;

    // Remove focus from any active element before proceeding
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    assignTasksMutation.mutate({
      tripId: selectedTripId,
      taskIds: selectedTaskIds,
    });
  };

  if (tasks.length === 0) return null;

  const activeTrips = trips?.data.filter(
    (trip) => trip.status === "ACTIVE" || trip.status === "PLANNED"
  );

  const selectedTrip = activeTrips?.find((trip) => trip.id === selectedTripId);

  return (
    <Card className="w-80">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">
          Selected Tasks ({selectedTaskIds.length}/{tasks.length})
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <div className="px-4 pb-2">
        <Select
          onValueChange={handleTripSelect}
          value={selectedTripId ?? undefined}
        >
          <SelectTrigger className="w-full mb-2">
            <SelectValue placeholder="Select a trip..." />
          </SelectTrigger>
          <SelectContent>
            {activeTrips?.map((trip) => (
              <SelectItem key={trip.id} value={trip.id}>
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 mr-2 rounded-sm"
                    style={{ backgroundColor: trip.color ?? "#CBD5E1" }}
                  />
                  {`Trip #${trip.tripNumber} - ${trip.operator?.user ? `${trip.operator.user.firstName} ${trip.operator.user.lastName}` : "?"}`}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedTripId && (
          <Button
            className="w-full mt-2"
            onClick={handleConfirmAssign}
            disabled={assignTasksMutation.isPending}
          >
            {assignTasksMutation.isPending
              ? "Assigning..."
              : `Confirm Assignment to Trip #${selectedTrip?.tripNumber}`}
          </Button>
        )}
      </div>
      <CardContent className="max-h-[60vh] overflow-y-auto pr-2 pt-2">
        <div className="space-y-2">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-start space-x-3 pb-3 border-b last:border-b-0"
            >
              <Checkbox
                checked={selectedTaskIds.includes(task.id)}
                onCheckedChange={(checked) => {
                  setSelectedTaskIds((prev) =>
                    checked
                      ? [...prev, task.id]
                      : prev.filter((id) => id !== task.id)
                  );
                }}
              />
              <div className="text-sm">
                <div className="font-medium">
                  Order #{task.order.orderNumber} -{" "}
                  {getTaskTypeDisplay(task.type)} Task #{task.taskNumber}
                </div>
                <div className="text-gray-600">
                  Sender: {task.order.senderName}
                </div>
                <div className="text-gray-600">
                  Recipient: {task.order.recipientName}
                </div>
                {task.address && (
                  <div className="text-gray-500 text-xs">
                    {task.address.line1}, {task.address.city}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
